'use client'

import { useState } from 'react'
import { motion } from 'framer-motion'
import { FaAndroid } from 'react-icons/fa'
import WaitlistModal from './WaitlistModal'

/**
 * AndroidWaitlistCard — the same dark "Android · early access" box, but the
 * action is a BUTTON that opens the name + email WaitlistModal (the home-page
 * pattern), instead of an inline single-field email form.
 *
 * Why the popup over the inline form here: the modal collects name + email and
 * is the established, higher-signal capture used on the homepage hero, so blog
 * and event readers get the same flow. The box stays visible (it's the pitch);
 * the button is the affordance.
 *
 * `surface` flows into the modal's tracking so waitlist signups attribute per
 * placement (blog_article_<topic>, events_page, …).
 */
export default function AndroidWaitlistCard({ surface = 'android_card' }: { surface?: string }) {
  const [open, setOpen] = useState(false)

  return (
    <section className="relative w-full py-12 sm:py-16">
      <div className="content">
        <motion.div
          className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-charcoal via-charcoal to-[#1f2937] p-6 sm:p-8 md:p-10 shadow-2xl shadow-charcoal/20"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.55 }}
        >
          {/* Decorative Android-green accent. Pure visual — aria-hidden. */}
          <div
            aria-hidden="true"
            className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-[#3DDC84]/15 blur-3xl"
          />
          <div className="relative grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 items-center">
            <div className="md:col-span-7">
              <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#3DDC84] mb-3">
                <FaAndroid className="w-3.5 h-3.5" aria-hidden="true" />
                Android · early access
              </p>
              <h2 className="text-[clamp(1.5rem,4vw,2rem)] font-bold text-white leading-[1.15] mb-3">
                Get the launch email the day Android opens.
              </h2>
              <p className="text-sm sm:text-base text-white/70 leading-relaxed">
                One email. No spam. We send when the Android app is in the
                Play Store, and never again unless you opt in.
              </p>
            </div>

            <div className="md:col-span-5 flex md:justify-end">
              <button
                type="button"
                onClick={() => setOpen(true)}
                className="inline-flex w-full sm:w-auto items-center justify-center bg-[#3DDC84] hover:bg-[#3DDC84]/90 text-charcoal font-semibold px-6 py-3.5 rounded-control transition-[color,background-color,border-color,text-decoration-color] duration-200 min-h-[48px] shadow-lg shadow-[#3DDC84]/30 pressable-sm"
              >
                Join the waitlist
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      <WaitlistModal isOpen={open} onClose={() => setOpen(false)} source={surface} />
    </section>
  )
}
